import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BookingTimeline from "@/components/BookingTimeline";
import { CreditCard, Lock, CheckCircle2, Apple, Smartphone, Banknote, Clipboard, ClipboardCheck, ArrowLeft } from "lucide-react";

export default function PaymentPage() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [method, setMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadBooking = async () => {
    try {
      const { data } = await api.get("/hotel/bookings");
      const b = (data.bookings || []).find((x) => x.id === bookingId);
      setBooking(b);
      if (b?.payment_status === "paid") setSuccess(true);
    } catch (e) {}
  };
  useEffect(() => { loadBooking(); }, [bookingId]); // eslint-disable-line

  /** Card / Apple Pay / Google Pay → Stripe Checkout redirect */
  const payOnline = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/hotel/bookings/${bookingId}/checkout-session`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  /** Cash: no Stripe, direct mark as paid */
  const payCash = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/hotel/bookings/${bookingId}/pay`, {
        booking_id: bookingId, method: "cash",
      });
      setBooking(data.booking);
      setSuccess(true);
      toast.success("Paiement en espèces enregistré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (method === "cash") return payCash();
    return payOnline();
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  if (!booking) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center">
        <p className="text-sm text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (success || booking.payment_status === "paid") {
    const trackLink = `${window.location.origin}/track/${booking.tracking_token}`;
    return (
      <div className="mx-auto max-w-4xl px-6 py-12 lg:px-10" data-testid="payment-success">
        <div className="rounded-2xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-6 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-[Outfit] text-2xl font-normal tracking-tight">Paiement confirmé</h1>
              <p className="text-sm text-muted-foreground">Vous suivez votre course en temps réel ci-dessous.</p>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1 truncate rounded-lg border border-border bg-card px-3 py-2 font-mono text-xs" data-testid="success-track-link">{trackLink}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => copyLink(trackLink)} className="gap-2" data-testid="success-copy-link">
                {copied ? <ClipboardCheck className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
                {copied ? "Copié" : "Copier le lien"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => nav("/dashboard/hotel/history")} className="gap-1" data-testid="success-back">
                <ArrowLeft className="h-4 w-4" /> Historique
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="mb-6 flex items-end justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-primary">Suivi de course</p>
              <h2 className="mt-1 font-[Outfit] text-2xl">{booking.client_name}</h2>
            </div>
            <Badge variant="outline">Mise à jour automatique</Badge>
          </div>
          <BookingTimeline token={booking.tracking_token} />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto grid max-w-5xl gap-8 px-6 py-12 md:grid-cols-12 lg:px-10" data-testid="payment-page">
      <div className="md:col-span-7">
        <p className="text-xs uppercase tracking-widest text-primary">Paiement</p>
        <h1 className="mt-2 font-[Outfit] text-3xl font-normal tracking-tight">Régler la course</h1>
        <p className="mt-2 text-sm text-muted-foreground flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Paiement en ligne sécurisé (3-D Secure)</p>

        <div className="mt-8 grid gap-3 sm:grid-cols-4">
          <MethodCard active={method === "card"} onClick={() => setMethod("card")} icon={<CreditCard className="h-5 w-5" />} label="Carte bancaire" testId="method-card" />
          <MethodCard active={method === "apple_pay"} onClick={() => setMethod("apple_pay")} icon={<Apple className="h-5 w-5" />} label="Apple Pay" testId="method-apple" />
          <MethodCard active={method === "google_pay"} onClick={() => setMethod("google_pay")} icon={<Smartphone className="h-5 w-5" />} label="Google Pay" testId="method-google" />
          <MethodCard active={method === "cash"} onClick={() => setMethod("cash")} icon={<Banknote className="h-5 w-5" />} label="Espèces" testId="method-cash" />
        </div>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="pay-form">
          {method !== "cash" && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 text-sm text-foreground">
              <p className="font-medium">Paiement en ligne sécurisé</p>
              <p className="mt-1 text-muted-foreground">
                Cliquez sur "Payer" pour être redirigé vers la page de paiement sécurisée. Cartes, Apple Pay et Google Pay sont acceptés.
              </p>
            </div>
          )}

          {method === "cash" && (
            <div className="rounded-xl border border-border bg-card p-5 text-sm">
              <p className="font-medium flex items-center gap-2"><Banknote className="h-4 w-4 text-accent" /> Paiement en espèces</p>
              <p className="mt-1 text-muted-foreground">
                La course sera marquée comme réglée. Le chauffeur encaissera directement le montant auprès du client (ou l'hôtel sur le compte).
              </p>
            </div>
          )}

          <Button type="submit" disabled={loading} size="lg" className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="pay-submit">
            {loading ? "Redirection..." : method === "cash" ? `Valider l'encaissement ${booking.price} €` : `Payer ${booking.price} €`}
          </Button>
        </form>
      </div>

      <aside className="md:col-span-5">
        <div className="sticky top-24 rounded-2xl border border-border bg-card p-6 shadow-sm">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Récapitulatif</p>
          <h3 className="mt-2 font-[Outfit] text-xl">Course Saor</h3>
          <dl className="mt-4 space-y-2 text-sm">
            <Row k="Départ" v={booking.departure_address} />
            <Row k="Arrivée" v={booking.arrival_address} />
            <Row k="Client" v={`${booking.client_name}`} />
            <Row k="Passagers" v={`${booking.passenger_count} pers · ${booking.luggage_count} bagages`} />
          </dl>
          <div className="mt-5 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-[Outfit] text-2xl">{booking.price} €</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function MethodCard({ active, onClick, icon, label, testId }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex flex-col items-center justify-center gap-2 rounded-xl border p-4 text-sm transition-all ${active ? "border-primary bg-primary text-primary-foreground shadow-sm" : "border-border bg-card hover:border-primary/40"}`}
      data-testid={testId}>
      {icon}
      <span>{label}</span>
    </button>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="max-w-[60%] text-right">{v}</dd>
    </div>
  );
}
